export interface ContractImportFinding {
  row: number | null;
  column: string | null;
  field: string;
  employeeNumber: string | null;
  value: string | number | boolean | null;
  code: string;
  message: string;
  relatedRows?: number[];
}

export interface ContractImportSummary {
  contractsCreated: number;
  contractsReused: number;
  schedulesCreated: number;
  employeesProcessed: number;
}

export type ContractImportResult =
  | { kind: 'rejected'; findings: ContractImportFinding[] }
  | { kind: 'success'; summary?: ContractImportSummary };

export const IMPORT_SUCCESS_MESSAGE = 'Los contratos se han subido con éxito.';
export const IMPORT_REJECTION_MESSAGE =
  'No se guardó ningún registro de esta carga. Corrige el archivo y vuelve a subirlo.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;
const isRow = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const isNullableText = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

const isFinding = (value: unknown): value is ContractImportFinding => {
  if (!isRecord(value)) return false;
  return (
    (value.row === null || isRow(value.row)) &&
    isNullableText(value.column) &&
    isNullableText(value.employeeNumber) &&
    hasText(value.field) &&
    hasText(value.code) &&
    hasText(value.message) &&
    (value.value === null ||
      typeof value.value === 'string' ||
      typeof value.value === 'boolean' ||
      (typeof value.value === 'number' && Number.isFinite(value.value))) &&
    (value.relatedRows === undefined ||
      (Array.isArray(value.relatedRows) && value.relatedRows.every(isRow)))
  );
};

const isSummary = (value: unknown): value is ContractImportSummary =>
  isRecord(value) &&
  [
    'contractsCreated',
    'contractsReused',
    'schedulesCreated',
    'employeesProcessed',
  ].every(
    (key) =>
      typeof value[key] === 'number' &&
      Number.isSafeInteger(value[key]) &&
      value[key] >= 0
  );

// Fail closed: never discard malformed findings and then claim zero writes.
// Project only display fields; sheet metadata is intentionally not exposed.
export const parseContractImportResult = (
  status: number,
  data: unknown
): ContractImportResult | undefined => {
  if (!isRecord(data)) return undefined;
  if (
    status === 422 &&
    data.code === 'CONTRACT_IMPORT_VALIDATION_FAILED' &&
    data.persisted === false &&
    hasText(data.message) &&
    Array.isArray(data.findings) &&
    data.findings.length > 0 &&
    data.findings.every(isFinding)
  ) {
    return {
      kind: 'rejected',
      findings: data.findings.map((finding) => ({
        row: finding.row,
        column: finding.column,
        field: finding.field,
        employeeNumber: finding.employeeNumber,
        value: finding.value,
        code: finding.code,
        message: finding.message,
        relatedRows: finding.relatedRows,
      })),
    };
  }
  if (
    status === 200 &&
    data.msg === IMPORT_SUCCESS_MESSAGE &&
    data.persisted !== false &&
    data.code === undefined &&
    data.findings === undefined &&
    data.invalidEmployeeIds === undefined &&
    data.invalidEmployeeSchedules === undefined
  ) {
    return {
      kind: 'success',
      summary: isSummary(data.summary)
        ? {
            contractsCreated: data.summary.contractsCreated,
            contractsReused: data.summary.contractsReused,
            schedulesCreated: data.summary.schedulesCreated,
            employeesProcessed: data.summary.employeesProcessed,
          }
        : undefined,
    };
  }
  return undefined;
};
